import { z } from "zod";

/**
 * The wire contract for the investments routes.
 *
 * MONEY AND SHARE COUNTS CROSS AS DECIMAL STRINGS, NOT NUMBERS. JSON has only doubles, and this
 * module's whole arithmetic rests on exactness: a share count is the multiplicand of every figure on
 * the page, and `price_per_share` is `numeric(15,4)` — the one money column in the schema that keeps
 * four decimal places, so that a penny stock at 0.0123 survives the round trip.
 *
 * NOTHING HERE REJECTS A PAST DATE. Backdating is a requirement — a household adopting GLADIS in 2026
 * has to be able to record what it bought in 2019. The FUTURE is rejected, in the domain, where the
 * refusal can say why: a price dated forward becomes the portfolio's current price the moment it is
 * written and outranks every real quote that follows it.
 */

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter a date as YYYY-MM-DD.");

/** Four decimal places, because that is what `numeric(15,4)` stores. */
const shares = z
  .string()
  .regex(/^\d{1,11}(\.\d{1,4})?$/, "Enter a number of shares like 10 or 10.2534.")
  .refine((value) => Number(value) > 0, "Enter a number of shares greater than zero.");

const price = z
  .string()
  .regex(/^\d{1,11}(\.\d{1,4})?$/, "Enter a price like 650.00.")
  .refine((value) => Number(value) > 0, "Enter a price greater than zero.");

/** Zero is legitimate and common — most brokers charge nothing on a sell. */
const fee = z.string().regex(/^\d{1,15}(\.\d{1,2})?$/, "Enter a fee like 2.50.");

const symbol = z
  .string()
  .trim()
  .min(1, "Enter a ticker symbol, like ASML.AS or AAPL.")
  .max(24)
  .regex(/^[A-Za-z0-9.\-^]+$/, "A ticker symbol uses letters, digits, dots and dashes.");

const name = z.string().trim().min(1, "Give the stock a name.").max(120);

export const AddStockRequestSchema = z.object({
  name,
  symbol,
  exchange: z.string().trim().max(80).nullish(),
});

export const UpdateStockRequestSchema = z
  .object({ name: name.optional(), symbol: symbol.optional() })
  .refine((body) => body.name !== undefined || body.symbol !== undefined, {
    message: "Change the name, the symbol, or both.",
  });

export const RecordTransactionRequestSchema = z.object({
  kind: z.enum(["buy", "sell"]),
  shares,
  pricePerShare: price,
  fee: fee.nullish(),
  date: isoDay,
});

export const UpdateTransactionRequestSchema = z
  .object({
    shares: shares.optional(),
    pricePerShare: price.optional(),
    fee: fee.optional(),
    date: isoDay.optional(),
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: "Change at least one field.",
  });

export const RecordPriceRequestSchema = z.object({ price, asOfDate: isoDay });

/**
 * The chart window, in months.
 *
 * Up to ten years. A hundred and twenty monthly points is a fine amount of data to draw and to send;
 * what it is not is a fine amount to compute per month with a query each, which is why the read
 * batches everything and folds in memory.
 *
 * The spec said "no range selector in v1". That was overturned on the household's feedback: a
 * portfolio is the one pillar where the interesting question is often about a decade rather than a
 * year, and the data to answer it is already there.
 */
export const InvestmentsPageQuerySchema = z.object({
  months: z.coerce.number().int().min(1).max(120).default(12),
});

/**
 * The page response.
 *
 * `null` IS THE CONTRACT FOR "UNPRICED", and it is not the same as zero. A client that coalesces
 * these to 0 reproduces exactly the defect AC-D9 exists to prevent: a holding the household owns,
 * reported as worth nothing, with no indication the figure is short.
 *
 * Parsed against a REAL response in an integration test — 009's rule, added after
 * `ExpensesPageDtoSchema` had drifted into fiction because nothing imported it.
 */
const PositionRowSchema = z.object({
  stockId: z.string(),
  name: z.string(),
  symbol: z.string(),
  sharesHeld: z.string(),
  averageCost: z.string().nullable(),
  currentPrice: z.string().nullable(),
  currentPriceAsOf: z.string().nullable(),
  currentValue: z.string().nullable(),
  unrealisedGain: z.string().nullable(),
  unrealisedGainPercent: z.string().nullable(),
  realisedGain: z.string(),
  costBasis: z.string(),
  sharesSold: z.string(),
  costOfSold: z.string(),
  proceeds: z.string(),
  hasTransactions: z.boolean(),
  canBeRemoved: z.boolean(),
});

export const InvestmentsPageDtoSchema = z.object({
  asOf: z.string(),
  totals: z.object({
    totalValue: z.string(),
    totalInvested: z.string(),
    unrealisedGain: z.string(),
    unrealisedGainPercent: z.string().nullable(),
    realisedGain: z.string(),
    unpricedHoldingCount: z.number().int().min(0),
  }),
  activePositions: z.array(PositionRowSchema),
  closedPositions: z.array(PositionRowSchema),
  chart: z.array(z.object({ month: z.string(), value: z.string() })),
  markers: z.array(
    z.object({
      month: z.string(),
      label: z.string(),
      direction: z.enum(["in", "out"]),
    }),
  ),
});

export const StockDetailDtoSchema = z.object({
  stock: z.object({
    id: z.string(),
    name: z.string(),
    symbol: z.string(),
    exchange: z.string().nullable(),
    canBeRemoved: z.boolean(),
    addedBy: z.string(),
    addedById: z.uuid().nullable(),
  }),
  position: z.object({
    sharesHeld: z.string(),
    averageCost: z.string().nullable(),
    costBasis: z.string(),
    currentPrice: z.string().nullable(),
    currentPriceAsOf: z.string().nullable(),
    currentValue: z.string().nullable(),
    unrealisedGain: z.string().nullable(),
    realisedGain: z.string(),
  }),
  transactions: z.array(
    z.object({
      id: z.string(),
      kind: z.enum(["buy", "sell"]),
      shares: z.string(),
      pricePerShare: z.string(),
      fee: z.string(),
      total: z.string(),
      date: z.string(),
      recordedBy: z.string(),
      recordedById: z.uuid().nullable(),
    }),
  ),
  prices: z.array(
    z.object({
      id: z.string(),
      price: z.string(),
      asOfDate: z.string(),
      source: z.enum(["member", "system"]),
      recordedBy: z.string().nullable(),
      recordedById: z.uuid().nullable(),
      canDelete: z.boolean(),
    }),
  ),
});

export type InvestmentsPageDto = z.infer<typeof InvestmentsPageDtoSchema>;
export type StockDetailDto = z.infer<typeof StockDetailDtoSchema>;
export type PositionRowDto = z.infer<typeof PositionRowSchema>;
